import { useTranslation } from 'react-i18next';

import { defaultLocale } from '~/utils/language';
import { getDefaultCurrency } from '~/utils/runtimeConfig';
import { useDefaultCurrency } from '~/utils/useChannelConfig';

const formatPrice = (priceInCents?: number): string => {
  if (priceInCents === undefined) return '-';
  return (priceInCents / 100).toFixed(2);
};

const currencyFormatter = (value?: number, currency?: string, locale = defaultLocale) => {
  if (value === undefined) return '-';
  const amount = value / 100;
  const currencyCode = currency ?? getDefaultCurrency();
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Locale-appropriate symbol for a currency code — `CZK` renders as `Kč` in Czech.
 * The API only ever sends the ISO code, which is not what a shopper expects to read.
 * Falls back to the code itself for locales that have no symbol for it.
 */
const currencySymbol = (currency?: string, locale = defaultLocale): string => {
  const currencyCode = currency ?? getDefaultCurrency();
  const symbol = new Intl.NumberFormat(locale, { style: 'currency', currency: currencyCode })
    .formatToParts(0)
    .find((part) => part.type === 'currency')?.value;
  return symbol ?? currencyCode;
};

function useFormatPrice() {
  const { i18n } = useTranslation();
  const locale = i18n.language ?? defaultLocale;
  const defaultCurrency = useDefaultCurrency();

  return (value?: number, currency?: string) => {
    const currencyCode = currency ?? defaultCurrency;
    return currencyFormatter(value, currencyCode, locale);
  };
}

export { formatPrice, currencyFormatter, currencySymbol, useFormatPrice };
